import { getCart, type CartLine } from '../cart/cartStore.ts';
import { clearSavedCommande, getSavedCommande, saveCommandeField, type SavedCommandeFields } from './commandeStore.ts';

interface CartLineResult {
  productId: string;
  colorId: string;
  sizeEu: string;
  sizeUs: string;
  quantity: number;
  name: string;
  slug: string;
  imageUrl: string;
  unitPrice: number;
  availableStock: number;
  isAvailable: boolean;
}

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address: string;
  addressComplement?: string;
  city: string;
  postalCode: string;
  country: string;
  phone?: string;
}

interface StripePaymentElement {
  mount(selector: string): void;
}

interface StripeElements {
  create(type: 'payment', options?: Record<string, unknown>): StripePaymentElement;
}

interface StripeConfirmResult {
  error?: { message?: string };
  paymentIntent?: { id: string; status: string };
}

interface StripeClient {
  elements(options: { clientSecret: string }): StripeElements;
  confirmPayment(options: {
    elements: StripeElements;
    confirmParams: { return_url: string };
    redirect: 'if_required';
  }): Promise<StripeConfirmResult>;
}

declare global {
  interface Window {
    Stripe?: (publishableKey: string) => StripeClient;
  }
}

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

/** Barre de récapitulatif repliable en mobile (≤900px, cf. commande.css) — le
 * bouton bascule .is-expanded pour révéler les lignes/totaux ; sans effet sur
 * desktop, où .commande__summary-toggle reste masqué et le récap est toujours
 * visible (sidebar sticky). */
function initSummaryToggle(): void {
  const summary = document.getElementById('commande-summary');
  const toggle = document.getElementById('commande-summary-toggle');
  if (!summary || !toggle) return;

  toggle.addEventListener('click', () => {
    const expanded = summary.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.querySelector('.commande__summary-toggle-label')!.textContent = expanded
      ? 'Masquer le récapitulatif'
      : 'Afficher le récapitulatif';
  });
}

async function fetchLines(cart: CartLine[]): Promise<CartLineResult[]> {
  const query = encodeURIComponent(JSON.stringify(cart));
  const res = await fetch(`/api/panier/lignes?lignes=${query}`);
  const body = await res.json();
  return body.data ?? [];
}

function loadStripeJs(): Promise<NonNullable<Window['Stripe']>> {
  return new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe);
      else reject(new Error('Stripe.js chargé mais indisponible.'));
    };
    script.onerror = () => reject(new Error('Impossible de charger Stripe.'));
    document.head.appendChild(script);
  });
}

async function init(): Promise<void> {
  const summaryLinesEl = document.getElementById('commande-summary-lines');
  const summaryEmptyEl = document.getElementById('commande-summary-empty');
  const summaryTotalsEl = document.getElementById('commande-summary-totals');
  const subtotalEl = document.getElementById('commande-subtotal');
  const shippingEl = document.getElementById('commande-shipping');
  const totalEl = document.getElementById('commande-total');
  const toggleTotalEl = document.getElementById('commande-summary-toggle-total');
  if (!summaryLinesEl || !summaryEmptyEl || !summaryTotalsEl || !subtotalEl || !shippingEl || !totalEl) return;

  initSummaryToggle();

  const cart = getCart();
  if (cart.length === 0) {
    summaryEmptyEl.hidden = false;
    return;
  }

  const lines = await fetchLines(cart);
  const availableLines = lines.filter((l) => l.isAvailable);
  if (availableLines.length === 0) {
    summaryEmptyEl.hidden = false;
    return;
  }

  const subtotal = availableLines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  const shippingCost = 0; // provisoire — calcul réel à définir avec la cliente

  summaryLinesEl.innerHTML = availableLines
    .map(
      (l) => `
        <li class="commande__summary-line">
          ${l.imageUrl ? `<img class="commande__summary-line-image" src="${l.imageUrl}" alt="${l.name}">` : ''}
          <div class="commande__summary-line-info">
            <span class="commande__summary-line-name">${l.name}</span>
            <span class="commande__summary-line-meta">Taille EU ${l.sizeEu} — Qté ${l.quantity}</span>
          </div>
          <span class="commande__summary-line-price">${formatPrice(l.unitPrice * l.quantity)}</span>
        </li>`,
    )
    .join('');
  subtotalEl.textContent = formatPrice(subtotal);
  shippingEl.textContent = formatPrice(shippingCost);
  totalEl.textContent = formatPrice(subtotal + shippingCost);
  summaryTotalsEl.hidden = false;
  if (toggleTotalEl) toggleTotalEl.textContent = formatPrice(subtotal + shippingCost);

  initSteps(cart);
}

function initSteps(cart: CartLine[]): void {
  const emailStep = document.getElementById('commande-step-email');
  const emailBody = document.getElementById('commande-email-body');
  const emailSummary = document.getElementById('commande-email-summary');
  const emailEditBtn = document.getElementById('commande-email-edit');
  const emailInput = document.getElementById('commande-email') as HTMLInputElement | null;
  const emailError = document.getElementById('commande-email-error');
  const emailContinueBtn = document.getElementById('commande-email-continue');

  const deliveryStep = document.getElementById('commande-step-delivery');
  const deliveryBody = document.getElementById('commande-delivery-body');
  const deliverySummary = document.getElementById('commande-delivery-summary');
  const deliveryEditBtn = document.getElementById('commande-delivery-edit');
  const deliveryError = document.getElementById('commande-delivery-error');
  const deliveryContinueBtn = document.getElementById('commande-delivery-continue');

  const paymentStep = document.getElementById('commande-step-payment');
  const paymentBody = document.getElementById('commande-payment-body');
  const paymentElementContainer = document.getElementById('commande-payment-element');
  const paymentUnavailable = document.getElementById('commande-payment-unavailable');
  const cgvCheckbox = document.getElementById('commande-cgv') as HTMLInputElement | null;
  const payBtn = document.getElementById('commande-pay-btn') as HTMLButtonElement | null;
  const paymentError = document.getElementById('commande-payment-error');

  if (
    !emailStep || !emailBody || !emailSummary || !emailEditBtn || !emailInput || !emailError || !emailContinueBtn ||
    !deliveryStep || !deliveryBody || !deliverySummary || !deliveryEditBtn || !deliveryError || !deliveryContinueBtn ||
    !paymentStep || !paymentBody || !paymentElementContainer || !paymentUnavailable || !cgvCheckbox || !payBtn || !paymentError
  ) {
    return;
  }

  let email = '';
  let shippingAddress: ShippingAddress | null = null;
  let stripe: StripeClient | null = null;
  let elements: StripeElements | null = null;
  let orderId: string | null = null;

  function getField(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? '';
  }

  // Préremplissage + persistance (survit à un refresh/fermeture) — jamais les
  // infos de carte, gérées par Stripe hors de notre DOM. Vidé à la commande
  // passée (voir payBtn plus bas), pour ne pas préremplir la commande suivante.
  const DELIVERY_FIELD_IDS: (keyof SavedCommandeFields)[] = [
    'firstName', 'lastName', 'address', 'addressComplement', 'city', 'postalCode', 'country', 'phone',
  ];

  const restoreSavedFields = (): void => {
    const saved = getSavedCommande();
    if (saved.email) emailInput.value = saved.email;
    for (const id of DELIVERY_FIELD_IDS) {
      const value = saved[id];
      const el = document.getElementById(`commande-${id}`) as HTMLInputElement | null;
      if (el && value) el.value = value;
    }
  };

  const wireFieldPersistence = (): void => {
    emailInput.addEventListener('input', () => saveCommandeField('email', emailInput.value));
    for (const id of DELIVERY_FIELD_IDS) {
      const el = document.getElementById(`commande-${id}`) as HTMLInputElement | null;
      el?.addEventListener('input', () => saveCommandeField(id, el.value));
    }
  };

  /** Anime l'apparition/disparition d'un bloc de step (collapse via max-height,
   * voir .commande__step-body--collapsed) — `hidden` n'est posé/retiré qu'au bon
   * moment (fin de la transition pour masquer, avant pour afficher) pour que
   * l'élément reste accessible/focusable pendant l'animation d'ouverture. */
  function setStepBodyVisible(body: HTMLElement, visible: boolean): void {
    if (visible) {
      if (!body.hidden) return;
      body.hidden = false;
      body.classList.add('commande__step-body--collapsed');
      void body.offsetHeight; // force le reflow pour partir bien de l'état masqué
      body.classList.remove('commande__step-body--collapsed');
    } else {
      if (body.hidden) return;
      body.classList.add('commande__step-body--collapsed');
      const onEnd = (e: TransitionEvent): void => {
        if (e.propertyName !== 'max-height') return;
        body.removeEventListener('transitionend', onEnd);
        body.hidden = true;
      };
      body.addEventListener('transitionend', onEnd);
    }
  }

  function lockStep(step: HTMLElement, body: HTMLElement): void {
    step.classList.add('commande__step--locked');
    setStepBodyVisible(body, false);
  }

  function openStep(step: HTMLElement, body: HTMLElement): void {
    step.classList.remove('commande__step--locked');
    step.classList.remove('commande__step--done');
    setStepBodyVisible(body, true);
  }

  /** `silent` : pas d'erreur affichée si invalide — utilisé pour retenter la
   * validation automatiquement au chargement (restauration post-refresh), sans
   * montrer d'erreur à une visiteuse qui n'a encore rien rempli. */
  const validateEmailStep = (options: { silent?: boolean } = {}): boolean => {
    const value = emailInput.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      if (!options.silent) emailError.hidden = false;
      return false;
    }
    emailError.hidden = true;
    email = value;

    emailStep.classList.add('commande__step--done');
    setStepBodyVisible(emailBody, false);
    emailSummary.hidden = false;
    emailSummary.innerHTML = `<p>Merci, vous recevrez les mises à jour sur <strong>${email}</strong>.</p>`;
    emailEditBtn.hidden = false;
    return true;
  };

  emailContinueBtn.addEventListener('click', () => {
    if (validateEmailStep()) openStep(deliveryStep, deliveryBody);
  });

  emailEditBtn.addEventListener('click', () => {
    openStep(emailStep, emailBody);
    emailSummary.hidden = true;
    emailEditBtn.hidden = true;
    lockStep(deliveryStep, deliveryBody);
    lockStep(paymentStep, paymentBody);
    deliverySummary.hidden = true;
    deliveryEditBtn.hidden = true;
  });

  const validateDeliveryStep = (options: { silent?: boolean } = {}): boolean => {
    const address: ShippingAddress = {
      firstName: getField('commande-firstName'),
      lastName: getField('commande-lastName'),
      address: getField('commande-address'),
      addressComplement: getField('commande-addressComplement') || undefined,
      city: getField('commande-city'),
      postalCode: getField('commande-postalCode'),
      country: getField('commande-country'),
      phone: getField('commande-phone') || undefined,
    };

    const required: (keyof ShippingAddress)[] = ['firstName', 'lastName', 'address', 'city', 'postalCode', 'country'];
    if (required.some((field) => !address[field])) {
      if (!options.silent) deliveryError.hidden = false;
      return false;
    }
    deliveryError.hidden = true;
    shippingAddress = address;

    deliveryStep.classList.add('commande__step--done');
    setStepBodyVisible(deliveryBody, false);
    deliverySummary.hidden = false;
    deliverySummary.innerHTML = `
      <p>${address.firstName} ${address.lastName}<br>
      ${address.address}${address.addressComplement ? `, ${address.addressComplement}` : ''}<br>
      ${address.postalCode} ${address.city}, ${address.country}${address.phone ? `<br>${address.phone}` : ''}</p>`;
    deliveryEditBtn.hidden = false;
    return true;
  };

  deliveryContinueBtn.addEventListener('click', () => {
    if (validateDeliveryStep()) {
      openStep(paymentStep, paymentBody);
      void initPayment(cart, email, shippingAddress!);
    }
  });

  deliveryEditBtn.addEventListener('click', () => {
    openStep(deliveryStep, deliveryBody);
    deliverySummary.hidden = true;
    deliveryEditBtn.hidden = true;
    lockStep(paymentStep, paymentBody);
  });

  function updatePayButtonState(): void {
    payBtn!.disabled = !cgvCheckbox!.checked || !elements;
  }

  cgvCheckbox.addEventListener('change', updatePayButtonState);

  async function initPayment(cartLines: CartLine[], customerEmail: string, address: ShippingAddress): Promise<void> {
    const stripeKey = paymentElementContainer!.dataset.stripeKey;

    const res = await fetch('/commande/intention-paiement', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: customerEmail, shippingAddress: address, lignes: cartLines }),
    });
    const body = await res.json();

    if (!res.ok || !body.success) {
      paymentError!.hidden = false;
      paymentError!.textContent = body.error ?? "Une erreur est survenue, merci de vérifier votre panier.";
      return;
    }

    orderId = body.data.orderId;

    if (!stripeKey) {
      paymentUnavailable!.hidden = false;
      return;
    }

    try {
      const Stripe = await loadStripeJs();
      stripe = Stripe(stripeKey);
      elements = stripe.elements({ clientSecret: body.data.clientSecret });
      elements.create('payment').mount('#commande-payment-element');
      updatePayButtonState();
    } catch {
      paymentUnavailable!.hidden = false;
    }
  }

  payBtn.addEventListener('click', async () => {
    if (!stripe || !elements || !orderId) return;
    paymentError.hidden = true;
    payBtn.disabled = true;

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/confirmation/${orderId}` },
      redirect: 'if_required',
    });

    if (result.error) {
      paymentError.hidden = false;
      paymentError.textContent = result.error.message ?? 'Le paiement a échoué.';
      payBtn.disabled = false;
      return;
    }

    clearSavedCommande();
    window.location.href = `/confirmation/${orderId}`;
  });

  // Restauration après un refresh/réouverture — préremplit d'abord les champs,
  // puis rejoue silencieusement la même validation que les boutons "Continuer"
  // pour retrouver l'état (étapes repliées + résumé, paiement réinitialisé) sans
  // rien faire retaper. S'arrête dès qu'une étape échoue (ex: email seul rempli).
  restoreSavedFields();
  wireFieldPersistence();
  if (validateEmailStep({ silent: true })) {
    openStep(deliveryStep, deliveryBody);
    if (validateDeliveryStep({ silent: true })) {
      openStep(paymentStep, paymentBody);
      void initPayment(cart, email, shippingAddress!);
    }
  }
}

void init();
